import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalytics, ANALYTICS_EVENTS } from '@/hooks/useAnalytics';
import { 
  Star, Calculator, FileText, Target, Bot, 
  ChevronRight, Play, Users, TrendingUp,
  Brain, MessageCircle, Navigation2, LineChart, Shield,
  Sparkles, ArrowRight, Trophy, Zap, CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import LoadedGuide from '@/components/loaded-guide/LoadedGuide';
import { EnhancedAIWorkspace } from '@/components/ai/EnhancedAIWorkspace';

interface IndexProps {
  workspace?: boolean;
  selectedAgent?: string;
}

const Index = ({ workspace = false, selectedAgent = 'loaded-bot' }: IndexProps) => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // If this is the workspace view, render the Enhanced AI Workspace
  if (workspace) {
    return (
      <div className="w-full min-h-screen">
        <EnhancedAIWorkspace />
      </div>
    );
  }

  const handleNavigation = (route: string, intent?: string) => {
    if (intent) {
      trackEvent(ANALYTICS_EVENTS.NAVIGATION_CLICKED, {
        destination: route,
        intent,
        source: 'homepage'
      });
    }
    navigate(route);
  };

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Immersive Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-purple-950/10 to-background overflow-hidden">
        {/* Dynamic Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div 
            className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-full blur-3xl floating-element"
            style={{ transform: `translateY(${scrollY * 0.1}px)` }}
          />
          <div 
            className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-r from-accent-cyan/20 to-accent-pink/20 rounded-full blur-3xl floating-element"
            style={{ transform: `translateY(${scrollY * -0.1}px)`, animationDelay: '2s' }}
          />
          <div 
            className="absolute top-1/2 left-1/3 w-64 h-64 bg-gradient-to-r from-accent-orange/15 to-accent-yellow/15 rounded-full blur-3xl floating-element"
            style={{ transform: `translateY(${scrollY * 0.05}px)`, animationDelay: '4s' }}
          />
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto space-y-8">
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold leading-tight">
              <span className="bg-gradient-to-r from-primary via-secondary to-accent-cyan bg-clip-text text-transparent animate-gradient-shift">
                Transform how the world
              </span>
              <br />
              <span className="text-text-primary">sees your talent</span>
            </h1>
            
            <p className="text-xl sm:text-2xl md:text-3xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
              Your career is the most important product you will ever build.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
              <Button 
                size="lg"
                onClick={() => handleNavigation('/score', 'get-started')}
                className="text-lg px-8 py-4 bg-gradient-to-r from-primary to-secondary hover:from-primary-hover hover:to-secondary-hover shadow-2xl hover:scale-105 transition-all duration-300"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Start Your Journey
              </Button>
              
              <Button 
                variant="ghost" 
                size="lg"
                onClick={() => handleNavigation('/workspace', 'ai-preview')}
                className="text-lg px-8 py-4 border border-border/50 hover:bg-card-hover group"
              >
                <Play className="mr-2 h-5 w-5 group-hover:text-primary transition-colors" />
                Preview AI Assistants
              </Button>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronRight className="h-6 w-6 text-text-muted rotate-90" />
        </div>
      </section>

      {/* Assessment Journey Section */}
      <section className="py-20 sm:py-32 bg-gradient-to-b from-background to-card-bg/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-accent-pink/20 to-primary/20 border border-accent-pink/30">
                  <Star className="h-4 w-4 text-accent-pink mr-2" />
                  <span className="text-sm font-medium text-accent-pink">Assessment</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-bold text-text-primary">
                  Discover your professional value
                </h2>
                <p className="text-xl text-text-secondary leading-relaxed">
                  Our comprehensive Loaded Score™ assessment evaluates you across 5 key dimensions, giving you a clear picture of your market value and growth potential.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <span className="text-text-primary">5-dimensional framework analysis</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-secondary rounded-full"></div>
                  <span className="text-text-primary">Personalized career stage mapping</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-accent-cyan rounded-full"></div>
                  <span className="text-text-primary">Actionable improvement insights</span>
                </div>
              </div>

              <Button 
                size="lg"
                onClick={() => handleNavigation('/score', 'assessment')}
                className="bg-gradient-to-r from-accent-pink to-primary hover:from-accent-pink/80 hover:to-primary/80 text-white group"
              >
                Start Your Assessment
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-card-bg to-card-hover rounded-2xl p-8 shadow-2xl border border-border/50">
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mb-4">
                      <span className="text-2xl font-bold text-white">85</span>
                    </div>
                    <h3 className="text-2xl font-bold text-text-primary">Your Loaded Score</h3>
                    <p className="text-text-secondary">Senior Professional Level</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {['Communication', 'Strategy', 'Technical', 'Leadership', 'Impact'].map((skill, index) => (
                      <div key={skill} className="bg-background/50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-text-secondary">{skill}</span>
                          <span className="text-sm font-medium text-primary">{85 + index * 2}</span>
                        </div>
                        <div className="w-full bg-border rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-primary to-secondary h-2 rounded-full transition-all duration-1000"
                            style={{ width: `${(85 + index * 2)}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rate Discovery Section */}
      <section className="py-20 sm:py-32 bg-gradient-to-b from-card-bg/30 to-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="bg-gradient-to-br from-card-bg to-card-hover rounded-2xl p-8 shadow-2xl border border-border/50">
                <div className="space-y-6">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-text-primary mb-4">Your Ideal Rate</h3>
                    <div className="text-6xl font-bold bg-gradient-to-r from-accent-orange to-accent-yellow bg-clip-text text-transparent">
                      $125
                    </div>
                    <p className="text-text-secondary">per hour</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-text-secondary">Desired lifestyle</span>
                      <span className="text-text-primary font-medium">$8,000/month</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-text-secondary">Work hours/week</span>
                      <span className="text-text-primary font-medium">20 hours</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-text-secondary">Market confidence</span>
                      <span className="text-primary font-medium">94% validated</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-8 order-1 lg:order-2">
              <div className="space-y-4">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-accent-orange/20 to-accent-yellow/20 border border-accent-orange/30">
                  <Calculator className="h-4 w-4 text-accent-orange mr-2" />
                  <span className="text-sm font-medium text-accent-orange">Calculator</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-bold text-text-primary">
                  Find your ideal freelance rate
                </h2>
                <p className="text-xl text-text-secondary leading-relaxed">
                  Start with your lifestyle goals, not industry averages. Our Rate Whisperer calculates your perfect hourly rate based on your desired income and work-life balance.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-accent-orange rounded-full"></div>
                  <span className="text-text-primary">Lifestyle-first approach</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-accent-yellow rounded-full"></div>
                  <span className="text-text-primary">Market validation & confidence building</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-accent-orange rounded-full"></div>
                  <span className="text-text-primary">Future income projections</span>
                </div>
              </div>

              <Button 
                size="lg"
                onClick={() => handleNavigation('/rate-whisperer', 'rate-calculator')}
                className="bg-gradient-to-r from-accent-orange to-accent-yellow hover:from-accent-orange/80 hover:to-accent-yellow/80 text-white group"
              >
                Calculate My Worth
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Career Documentation Section */}
      <section className="py-20 sm:py-32 bg-gradient-to-b from-background to-card-bg/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30">
                  <FileText className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm font-medium text-green-500">Documentation</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-bold text-text-primary">
                  Build your career story
                </h2>
                <p className="text-xl text-text-secondary leading-relaxed">
                  Document your professional journey with our comprehensive portfolio tools. Track achievements, showcase growth, and build compelling career narratives.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-text-primary">Career timeline and milestone tracking</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  <span className="text-text-primary">Achievement portfolio builder</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-text-primary">Professional growth visualization</span>
                </div>
              </div>

              <Button 
                size="lg"
                onClick={() => handleNavigation('/artifacts', 'career-documentation')}
                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white group"
              >
                Document My Journey
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-card-bg to-card-hover rounded-2xl p-8 shadow-2xl border border-border/50">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg">
                    <Trophy className="h-6 w-6 text-green-500" />
                    <div>
                      <div className="font-semibold text-text-primary">Project Leadership Award</div>
                      <div className="text-sm text-text-muted">Led cross-functional team of 12</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-emerald-500/10 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-emerald-500" />
                    <div>
                      <div className="font-semibold text-text-primary">Revenue Growth Achievement</div>
                      <div className="text-sm text-text-muted">Increased sales by 340% in Q3</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg">
                    <Users className="h-6 w-6 text-green-500" />
                    <div>
                      <div className="font-semibold text-text-primary">Team Development</div>
                      <div className="text-sm text-text-muted">Mentored 8 junior developers</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Decision Intelligence Section */}
      <section className="py-20 sm:py-32 bg-gradient-to-b from-card-bg/30 to-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="bg-gradient-to-br from-card-bg to-card-hover rounded-2xl p-8 shadow-2xl border border-border/50">
                <h4 className="text-lg font-semibold text-text-primary mb-6">Opportunity Assessment Matrix</h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-text-secondary">Growth Potential</span>
                    <div className="flex gap-1">
                      {[1,2,3,4,5].map(i => (
                        <div key={i} className={`w-3 h-3 rounded-full ${i <= 4 ? 'bg-primary' : 'bg-muted'}`}></div>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-secondary">Cultural Fit</span>
                    <div className="flex gap-1">
                      {[1,2,3,4,5].map(i => (
                        <div key={i} className={`w-3 h-3 rounded-full ${i <= 3 ? 'bg-accent' : 'bg-muted'}`}></div>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-secondary">Compensation</span>
                    <div className="flex gap-1">
                      {[1,2,3,4,5].map(i => (
                        <div key={i} className={`w-3 h-3 rounded-full ${i <= 5 ? 'bg-primary' : 'bg-muted'}`}></div>
                      ))}
                    </div>
                  </div>
                  <div className="border-t border-border/30 pt-4">
                    <div className="flex justify-between items-center font-bold">
                      <span className="text-text-primary">Overall Score</span>
                      <span className="text-primary text-xl">8.2/10</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-8 order-1 lg:order-2">
              <div className="space-y-4">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-500/30">
                  <Target className="h-4 w-4 text-indigo-500 mr-2" />
                  <span className="text-sm font-medium text-indigo-500">Decision Intelligence</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-bold text-text-primary">
                  Make smarter career decisions
                </h2>
                <p className="text-xl text-text-secondary leading-relaxed">
                  Stop second-guessing career moves. Our opportunity assessment framework helps you evaluate options objectively and make confident decisions.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <span className="text-text-primary">Objective scoring framework</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span className="text-text-primary">Multi-criteria decision analysis</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <span className="text-text-primary">Risk assessment and mitigation</span>
                </div>
              </div>

              <Button 
                size="lg"
                onClick={() => handleNavigation('/opportunity-audit', 'decision-intelligence')}
                className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white group"
              >
                Evaluate Opportunities
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Power Moves & Planning Section */}
      <section className="py-20 sm:py-32 bg-gradient-to-b from-background to-card-bg/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-pink-500/20 to-rose-500/20 border border-pink-500/30">
                  <Zap className="h-4 w-4 text-pink-500 mr-2" />
                  <span className="text-sm font-medium text-pink-500">Strategic Planning</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-bold text-text-primary">
                  Plan your power moves
                </h2>
                <p className="text-xl text-text-secondary leading-relaxed">
                  Transform career aspirations into actionable strategies. Track initiatives, set meaningful goals, and execute your professional roadmap.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                <Button 
                  variant="outline" 
                  onClick={() => handleNavigation('/power-moves', 'power-moves')}
                  className="h-20 flex flex-col items-center justify-center border-2 border-pink-500 text-pink-500 hover:bg-pink-500 hover:text-white transition-all duration-300"
                >
                  <Zap className="h-6 w-6 mb-2" />
                  Power Moves
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleNavigation('/okrs', 'okrs')}
                  className="h-20 flex flex-col items-center justify-center border-2 border-rose-500 text-rose-500 hover:bg-rose-500 hover:text-white transition-all duration-300"
                >
                  <CheckCircle className="h-6 w-6 mb-2" />
                  OKRs
                </Button>
              </div>

              <Button 
                size="lg"
                onClick={() => handleNavigation('/big-sheet', 'strategic-planning')}
                className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white group"
              >
                Start Planning
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-card-bg to-card-hover rounded-2xl p-8 shadow-2xl border border-border/50">
                <h4 className="text-lg font-semibold text-text-primary mb-6">Strategic Initiatives</h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-pink-500/10 rounded-lg border-l-4 border-pink-500">
                    <div className="w-3 h-3 bg-pink-500 rounded-full"></div>
                    <div className="flex-1">
                      <div className="font-medium text-text-primary">Build Personal Brand</div>
                      <div className="text-sm text-text-muted">In Progress • 70% complete</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-rose-500/10 rounded-lg border-l-4 border-rose-500">
                    <div className="w-3 h-3 bg-rose-500 rounded-full"></div>
                    <div className="flex-1">
                      <div className="font-medium text-text-primary">Leadership Certification</div>
                      <div className="text-sm text-text-muted">Planned • Starting Q2</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg border-l-4 border-green-500">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <div className="flex-1">
                      <div className="font-medium text-text-primary">Network Expansion</div>
                      <div className="text-sm text-text-muted">Completed • 150% of goal</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 sm:py-32 bg-gradient-to-b from-background to-purple-950/10">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-8 mb-16">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border border-purple-500/30">
              <Brain className="h-4 w-4 text-purple-500 mr-2" />
              <span className="text-sm font-medium text-purple-500">AI Guidance</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-text-primary max-w-3xl mx-auto">
              Meet your AI career team
            </h2>
            <p className="text-xl text-text-secondary max-w-2xl mx-auto leading-relaxed">
              Get personalized career guidance from 4 specialized AI assistants, each with unique expertise and personality.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              {
                name: 'LoadedBot',
                icon: Bot,
                specialty: 'General career guidance',
                color: 'from-blue-500 to-cyan-500',
                description: 'Your comprehensive career companion for all professional questions.'
              },
              {
                name: 'Career Navigator',
                icon: Navigation2,
                specialty: 'Strategic planning',
                color: 'from-purple-500 to-pink-500',
                description: 'Expert in long-term career strategy and professional trajectory planning.'
              },
              {
                name: 'Score Booster',
                icon: LineChart,
                specialty: 'Skill development',
                color: 'from-green-500 to-emerald-500',
                description: 'Focused on improving your Loaded Score and professional capabilities.'
              },
              {
                name: 'Future Proof Advisor',
                icon: Shield,
                specialty: 'Industry insights',
                color: 'from-orange-500 to-red-500',
                description: 'Keeps you ahead of industry trends and future-ready.'
              }
            ].map((assistant, index) => (
              <div key={assistant.name} className="bg-card-bg rounded-xl p-6 border border-border/50 hover:border-primary/30 transition-all duration-300 hover:scale-105">
                <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${assistant.color} flex items-center justify-center mb-4`}>
                  <assistant.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-text-primary mb-2">{assistant.name}</h3>
                <p className="text-sm text-primary font-medium mb-2">{assistant.specialty}</p>
                <p className="text-sm text-text-secondary leading-relaxed">{assistant.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button 
              size="lg"
              onClick={() => handleNavigation('/workspace', 'ai-team')}
              className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white group"
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              Meet Your AI Career Team
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 sm:py-32 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-4xl sm:text-5xl font-bold text-text-primary">
              Ready to transform your career?
            </h2>
            <p className="text-xl text-text-secondary leading-relaxed">
              Join thousands of professionals who have discovered their true value and built the career they deserve.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => handleNavigation('/score', 'final-cta')}
                className="bg-gradient-to-r from-primary to-secondary hover:from-primary-hover hover:to-secondary-hover text-lg px-8 py-4 shadow-2xl hover:scale-105 transition-all duration-300"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Get Started Now
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Index;